import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  CreditCard,
  Download,
  Loader2,
  Crown,
  Calendar,
  CheckCircle,
  ArrowUp,
  ArrowDown,
  Zap,
  Sparkles,
  Image,
  Gauge,
  Headphones,
  Users,
  Code,
  AlertTriangle,
  Gift,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription, SUBSCRIPTION_TIERS, SubscriptionTier } from "@/hooks/useSubscription";
import { toast } from "sonner";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const TIER_ORDER: SubscriptionTier[] = ['basic', 'pro', 'enterprise'];

const ANNUAL_DISCOUNT = 0.20; // 20% off

const TIER_BENEFITS: Record<string, { icon: React.ElementType; label: string }[]> = {
  basic: [
    { icon: Image, label: "50 miniatures/mois" },
    { icon: Sparkles, label: "Qualité HD" },
    { icon: Zap, label: "Styles de base" },
    { icon: Headphones, label: "Support email" },
  ],
  pro: [
    { icon: Image, label: "Miniatures illimitées" },
    { icon: Sparkles, label: "Qualité 4K" },
    { icon: Gauge, label: "IA Ultra-rapide" },
    { icon: Headphones, label: "Support Prioritaire" },
    { icon: Zap, label: "A/B testing intégré" },
    { icon: Crown, label: "Brand Kit" },
  ],
  enterprise: [
    { icon: Image, label: "Tout du plan Pro" },
    { icon: Users, label: "5 membres d'équipe" },
    { icon: Code, label: "Accès API" },
    { icon: Headphones, label: "Account Manager dédié" },
    { icon: Sparkles, label: "Export white-label" },
    { icon: Zap, label: "Intégrations custom" },
  ],
};

const Billing = () => {
  const { user, loading } = useAuth();
  const { 
    subscribed, 
    tier, 
    subscriptionEnd, 
    openCustomerPortal, 
    createCheckout,
    loading: subLoading 
  } = useSubscription();
  const navigate = useNavigate();
  const [portalLoading, setPortalLoading] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<SubscriptionTier>(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingTier, setPendingTier] = useState<SubscriptionTier>(null);
  const [retentionModalOpen, setRetentionModalOpen] = useState(false);
  const [promoApplied, setPromoApplied] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true);
      await openCustomerPortal();
    } catch (error) {
      toast.error("Failed to open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  const handleSwitchPlan = async (targetTier: SubscriptionTier) => {
    if (!targetTier) return;
    try {
      setSwitchingTo(targetTier);
      await openCustomerPortal();
      toast.success("Redirecting to manage your subscription...");
    } catch (error) {
      toast.error("Failed to switch plan");
    } finally {
      setSwitchingTo(null);
    }
  };

  const handleSubscribeClick = (targetTier: SubscriptionTier) => {
    if (!targetTier) return;
    setPendingTier(targetTier);
    setConfirmModalOpen(true);
  };

  const handleConfirmSubscribe = async () => {
    if (!pendingTier) return;
    setConfirmModalOpen(false);
    try {
      setSwitchingTo(pendingTier);
      await createCheckout(SUBSCRIPTION_TIERS[pendingTier].price_id);
    } catch (error) {
      toast.error("Failed to start checkout");
    } finally {
      setSwitchingTo(null);
      setPendingTier(null);
    }
  };

  const getTierIndex = (t: SubscriptionTier): number => {
    if (!t) return -1;
    return TIER_ORDER.indexOf(t);
  };

  const isCurrentPlan = (t: SubscriptionTier): boolean => tier === t;
  const isUpgrade = (t: SubscriptionTier): boolean => {
    if (!tier || !t) return true;
    return getTierIndex(t) > getTierIndex(tier);
  };

  const getDisplayPrice = (tierKey: string): string => {
    const base = SUBSCRIPTION_TIERS[tierKey as keyof typeof SUBSCRIPTION_TIERS].price;
    if (isAnnual) {
      return (base * (1 - ANNUAL_DISCOUNT)).toFixed(2);
    }
    return base.toFixed(2);
  };

  const firstName = user?.user_metadata?.full_name?.split(' ')[0] || "Créateur";

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Mock invoices
  const invoices = subscribed ? [
    { id: "INV-001", date: "Jan 12, 2026", amount: tier ? `$${SUBSCRIPTION_TIERS[tier].price.toFixed(2)}` : "$29.99", status: "Paid" },
    { id: "INV-002", date: "Dec 12, 2025", amount: tier ? `$${SUBSCRIPTION_TIERS[tier].price.toFixed(2)}` : "$29.99", status: "Paid" },
  ] : [];

  return (
    <DashboardLayout activeTab="billing">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Billing</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Manage your subscription and payment methods
              </p>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 max-w-5xl">
          {/* Current Plan Summary */}
          <div className={`p-6 rounded-xl border mb-6 ${
            subscribed 
              ? "bg-gradient-to-br from-green-500/10 to-card border-green-500/30" 
              : "bg-card border-border"
          }`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  subscribed ? "bg-green-500/20" : "bg-primary/10"
                }`}>
                  <Crown className={`w-6 h-6 ${subscribed ? "text-green-500" : "text-primary"}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">
                    {subscribed && tier ? SUBSCRIPTION_TIERS[tier].name : "Free Plan"}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {subscribed ? "Active subscription" : "Limited features"}
                  </p>
                </div>
              </div>
              {subscribed && (
                <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-500 text-sm font-medium">
                  Active
                </span>
              )}
            </div>

            {subscribed && subscriptionEnd && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Calendar className="w-4 h-4" />
                <span>
                  Renews on {new Date(subscriptionEnd).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
            )}

            {subscribed && (
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={handleManageSubscription}
                  disabled={portalLoading || subLoading}
                >
                  {portalLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  Manage Payment Method
                </Button>
                <Button 
                  variant="ghost" 
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => setRetentionModalOpen(true)}
                >
                  Cancel Subscription
                </Button>
              </div>
            )}
          </div>

          {/* Billing Toggle: Monthly vs Annual */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className={`text-sm font-medium transition-colors ${!isAnnual ? 'text-foreground' : 'text-muted-foreground'}`}>
              Mensuel
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
                isAnnual 
                  ? 'bg-gradient-to-r from-purple-600 to-fuchsia-500' 
                  : 'bg-secondary'
              }`}
              aria-label="Toggle annual billing"
            >
              <div className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-300 ${
                isAnnual ? 'translate-x-7' : 'translate-x-0'
              }`} />
            </button>
            <span className={`text-sm font-medium transition-colors ${isAnnual ? 'text-foreground' : 'text-muted-foreground'}`}>
              Annuel
            </span>
            {isAnnual && (
              <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-green-500/20 text-green-400 border border-green-500/30 animate-scale-in">
                ÉCONOMISEZ 20%
              </span>
            )}
          </div>

          {/* Plan Cards */}
          <div className="mb-6">
            <h3 className="font-semibold text-lg mb-4">
              {subscribed ? "Switch Plan" : "Choose a Plan"}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {TIER_ORDER.map((tierKey) => {
                if (!tierKey) return null;
                const tierData = SUBSCRIPTION_TIERS[tierKey];
                const isCurrent = isCurrentPlan(tierKey);
                const upgrade = isUpgrade(tierKey);
                const isRecommended = tierKey === 'pro';
                const benefits = TIER_BENEFITS[tierKey] || [];
                
                return (
                  <div 
                    key={tierKey}
                    className={`relative p-5 rounded-xl border transition-all duration-300 ${
                      isCurrent 
                        ? "bg-primary/5 border-primary ring-2 ring-primary/20" 
                        : isRecommended
                        ? "bg-card border-primary/50 shadow-[0_0_30px_hsl(262,83%,58%,0.15)] hover:shadow-[0_0_40px_hsl(262,83%,58%,0.25)]"
                        : "bg-card border-border hover:border-primary/50"
                    }`}
                  >
                    {/* Recommended badge */}
                    {isRecommended && !isCurrent && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <div className="px-3 py-1 rounded-full gradient-bg text-xs font-bold text-primary-foreground flex items-center gap-1 shadow-lg">
                          <Sparkles className="w-3 h-3" />
                          Recommandé
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold">{tierData.name}</h4>
                      {isCurrent && (
                        <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-medium">
                          Current
                        </span>
                      )}
                    </div>
                    
                    <div className="mb-1">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold transition-all duration-500">
                          ${getDisplayPrice(tierKey)}
                        </span>
                        <span className="text-muted-foreground">/month</span>
                      </div>
                      {isAnnual && (
                        <p className="text-xs text-muted-foreground mt-1 line-through">
                          ${tierData.price.toFixed(2)}/month
                        </p>
                      )}
                    </div>

                    {/* Benefits with icons */}
                    <ul className="space-y-2.5 my-5">
                      {benefits.map((b, idx) => (
                        <li key={idx} className="flex items-center gap-2.5 text-sm">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                            isRecommended ? 'bg-primary/20' : 'bg-green-500/20'
                          }`}>
                            <b.icon className={`w-3 h-3 ${isRecommended ? 'text-primary' : 'text-green-500'}`} />
                          </div>
                          <span>{b.label}</span>
                        </li>
                      ))}
                    </ul>

                    {isCurrent ? (
                      <Button variant="outline" className="w-full" disabled>
                        Current Plan
                      </Button>
                    ) : subscribed ? (
                      <Button 
                        variant={upgrade ? "gradient" : "outline"}
                        className="w-full"
                        onClick={() => handleSwitchPlan(tierKey)}
                        disabled={switchingTo === tierKey || subLoading}
                      >
                        {switchingTo === tierKey ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : upgrade ? (
                          <>
                            <ArrowUp className="w-4 h-4" />
                            Upgrade
                          </>
                        ) : (
                          <>
                            <ArrowDown className="w-4 h-4" />
                            Downgrade
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button 
                        variant={isRecommended ? "gradient" : "outline"}
                        className={`w-full ${isRecommended ? 'btn-shimmer' : ''}`}
                        onClick={() => handleSubscribeClick(tierKey)}
                        disabled={switchingTo === tierKey || subLoading}
                      >
                        {switchingTo === tierKey ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Crown className="w-4 h-4" />
                            Subscribe
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Billing History */}
          {subscribed && invoices.length > 0 && (
            <div className="p-6 rounded-xl bg-card border border-border">
              <h3 className="font-semibold mb-4">Billing History</h3>
              <div className="space-y-3">
                {invoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-secondary/50"
                  >
                    <div>
                      <p className="font-medium">{invoice.id}</p>
                      <p className="text-sm text-muted-foreground">{invoice.date}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-semibold">{invoice.amount}</span>
                      <span className="px-2 py-1 rounded bg-green-500/20 text-green-500 text-xs font-medium">
                        {invoice.status}
                      </span>
                      <Button variant="ghost" size="sm">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      {/* Confirmation Modal ("The Closer") */}
      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent className="sm:max-w-md border-primary/30 bg-card">
          <DialogHeader>
            <DialogTitle className="text-xl">
              Passez au niveau supérieur, {firstName} ! 🚀
            </DialogTitle>
            <DialogDescription className="text-muted-foreground pt-2">
              Rejoignez plus de 500 créateurs qui boostent leur CTR avec ThumbGenius Pro. Vos prochaines miniatures sont prêtes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Plan summary */}
            {pendingTier && (
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{SUBSCRIPTION_TIERS[pendingTier].name}</span>
                  <span className="text-lg font-bold gradient-text">
                    ${getDisplayPrice(pendingTier)}/mo
                  </span>
                </div>
                {isAnnual && (
                  <p className="text-xs text-green-400 mt-1">20% de réduction appliquée (facturation annuelle)</p>
                )}
              </div>
            )}

            {/* Social proof */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
              <Zap className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                <span className="text-foreground font-medium">Plus de 80% des utilisateurs Pro</span> doublent leurs vues en moins d'un mois.
              </p>
            </div>

            <Button 
              variant="gradient" 
              size="lg" 
              className="w-full btn-shimmer font-bold text-base gap-2"
              onClick={handleConfirmSubscribe}
              disabled={switchingTo !== null}
            >
              {switchingTo ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Crown className="w-5 h-5" />
                  Confirmer mon abonnement
                </>
              )}
            </Button>

            <p className="text-[11px] text-center text-muted-foreground">
              Annulable à tout moment. Paiement sécurisé via Stripe.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Retention Modal */}
      <Dialog open={retentionModalOpen} onOpenChange={setRetentionModalOpen}>
        <DialogContent className="sm:max-w-md border-amber-500/30 bg-card">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Êtes-vous sûr de vouloir partir ?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground pt-2">
              Voici ce que vous perdrez en annulant votre abonnement :
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Lost benefits */}
            <div className="space-y-2">
              {[
                "Miniatures illimitées",
                "Export en qualité 4K",
                "IA Ultra-rapide (priorité de file)",
                "Support prioritaire",
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-destructive/5 border border-destructive/10">
                  <span className="text-destructive">✕</span>
                  <span className="text-muted-foreground line-through">{b}</span>
                </div>
              ))}
            </div>

            {/* Promo offer */}
            {!promoApplied ? (
              <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-card border border-emerald-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-5 h-5 text-emerald-400" />
                  <span className="font-semibold text-emerald-400">Offre spéciale rétention</span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Restez et bénéficiez de <span className="text-foreground font-bold">-50% pendant 2 mois</span> avec le code :
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 rounded-lg bg-background/50 border border-emerald-500/20 text-emerald-400 font-mono text-sm text-center">
                    STAY50
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-400"
                    onClick={() => {
                      setPromoApplied(true);
                      toast.success("Code STAY50 appliqué ! -50% pendant 2 mois.");
                    }}
                  >
                    Appliquer
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-emerald-400 shrink-0" />
                <div>
                  <p className="font-semibold text-emerald-400">Code appliqué !</p>
                  <p className="text-xs text-muted-foreground">-50% sur vos 2 prochains mois.</p>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                variant="gradient"
                className="flex-1 font-bold"
                onClick={() => {
                  setRetentionModalOpen(false);
                  toast.success("Merci de rester avec nous ! 🎉");
                }}
              >
                Garder mon abonnement
              </Button>
              <Button
                variant="ghost"
                className="text-muted-foreground hover:text-destructive text-xs"
                onClick={() => {
                  setRetentionModalOpen(false);
                  handleManageSubscription();
                }}
              >
                Annuler quand même
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Billing;
